<template>
  <!--
    لاگ ریز بازدیدها: پل بین نمودارهای بالای صفحه و «همان چند درخواست مشکوک».
    با همان فیلترهای صفحه کار می‌کند، پس هر برشی که بالا انتخاب شود اینجا هم
    اعمال است.
  -->
  <b-card
    no-body
    class="visit-log"
  >
    <b-card-body>
      <div class="d-flex justify-content-between align-items-start flex-wrap mb-1">
        <div>
          <h4 class="mb-25">
            ریز بازدیدها
          </h4>
          <div class="visit-log__subtitle">
            <template v-if="total">
              {{ formatNumber(total) }} درخواست با فیلترهای فعلی — روی هر ردیف بزنید تا جزئیات کامل را ببینید
            </template>
            <template v-else>
              تک‌تک درخواست‌های ثبت‌شده، با همان فیلترهای بالای صفحه
            </template>
          </div>
        </div>

        <div class="d-flex align-items-end">
          <div class="mr-1">
            <label class="visit-log__label">ترتیب</label>
            <b-form-select
              :value="sort"
              :options="sortOptions"
              size="sm"
              @change="$emit('sort', $event)"
            />
          </div>
          <div>
            <label class="visit-log__label">تعداد</label>
            <b-form-select
              :value="perPage"
              :options="perPageOptions"
              size="sm"
              @change="$emit('per-page', $event)"
            />
          </div>
        </div>
      </div>

      <div
        class="visit-log__body"
        :class="{ 'visit-log__body--busy': loading }"
      >
        <b-table
          v-if="rows.length"
          :items="rows"
          :fields="fields"
          small
          hover
          responsive
          class="mb-1 visit-log__table"
          @row-clicked="open"
        >
          <template #cell(Date)="data">
            <span class="visit-log__mono text-nowrap">{{ data.item.Date }}</span>
          </template>

          <template #cell(Path)="data">
            <div class="visit-log__path">
              <span :title="data.item.Url">{{ data.item.Path }}</span>
              <span
                v-if="data.item.UrlKind"
                class="visit-log__sub"
              >{{ data.item.UrlKind }}</span>
            </div>
          </template>

          <template #cell(StatusCode)="data">
            <b-badge
              :variant="statusVariant(data.item.StatusCode)"
              :title="data.item.StatusTitle"
            >
              {{ data.item.StatusCode }}
            </b-badge>
          </template>

          <template #cell(Duration)="data">
            <span class="visit-log__mono text-nowrap">{{ formatNumber(data.item.Duration) }} ms</span>
          </template>

          <template #cell(Device)="data">
            <div class="visit-log__path">
              <span>{{ data.item.Bot || data.item.Device || '—' }}</span>
              <span
                v-if="data.item.Platform"
                class="visit-log__sub"
              >{{ data.item.Platform }}</span>
            </div>
          </template>

          <template #cell(Ip)="data">
            <div class="visit-log__path">
              <span class="visit-log__mono">{{ data.item.Ip || '—' }}</span>
              <span class="visit-log__sub">{{ data.item.UserTitle || 'مهمان' }}</span>
            </div>
          </template>
        </b-table>

        <div
          v-else
          class="visit-log__empty"
        >
          <feather-icon
            icon="InboxIcon"
            size="26"
          />
          <p class="mt-1 mb-0">
            بازدیدی با این فیلترها ثبت نشده است
          </p>
        </div>

        <z-pagination
          v-if="total > perPage"
          :page="page"
          :per-page="perPage"
          :total-rows="total"
          :loading="loading"
          @change="$emit('page', $event)"
        />
      </div>
    </b-card-body>

    <!-- جزئیات کامل؛ بدنه و هدر جدا گرفته می‌شوند چون می‌توانند بزرگ باشند -->
    <b-modal
      v-model="showDetail"
      title="جزئیات بازدید"
      size="lg"
      ok-only
      ok-title="بستن"
      scrollable
    >
      <div
        v-if="detailLoading"
        class="text-center py-3"
      >
        <b-spinner variant="primary" />
      </div>

      <div v-else-if="detail">
        <dl class="visit-log__detail">
          <template v-for="item in detailRows">
            <dt :key="`t-${item.label}`">
              {{ item.label }}
            </dt>
            <dd :key="`d-${item.label}`">
              <span :class="{ 'visit-log__mono': item.mono }">{{ item.value || '—' }}</span>
            </dd>
          </template>
        </dl>

        <template v-for="block in detailBlocks">
          <div
            v-if="block.value"
            :key="block.label"
            class="mt-1"
          >
            <div class="visit-log__label">
              {{ block.label }}
            </div>
            <pre class="visit-log__pre">{{ block.value }}</pre>
          </div>
        </template>
      </div>

      <div
        v-else
        class="text-center text-muted py-3"
      >
        این بازدید پیدا نشد
      </div>
    </b-modal>
  </b-card>
</template>

<script>
import { BCardBody, BFormSelect, BModal } from 'bootstrap-vue'

export default {
  name: 'ReportVisitLog',
  components: { BCardBody, BFormSelect, BModal },
  props: {
    // خروجی report/traffic/log
    log: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    sort: {
      type: String,
      default: 'recent',
    },
  },
  data() {
    return {
      showDetail: false,
      detailLoading: false,
      detail: null,
      sortOptions: [
        { value: 'recent', text: 'جدیدترین' },
        { value: 'oldest', text: 'قدیمی‌ترین' },
        { value: 'slow', text: 'کندترین' },
      ],
      perPageOptions: [10, 25, 50, 100, 200],
      fields: [
        { key: 'Date', label: 'زمان' },
        { key: 'Path', label: 'آدرس' },
        { key: 'StatusCode', label: 'وضعیت' },
        { key: 'Duration', label: 'مدت' },
        { key: 'Device', label: 'دستگاه' },
        { key: 'Ip', label: 'کاربر' },
      ],
    }
  },
  computed: {
    rows() {
      return (this.log && this.log.Rows) || []
    },

    total() {
      return (this.log && this.log.Total) || 0
    },

    page() {
      return (this.log && this.log.Page) || 1
    },

    perPage() {
      return (this.log && this.log.PerPage) || 25
    },

    detailRows() {
      const item = this.detail

      if (!item) return []

      return [
        { label: 'زمان', value: item.Date, mono: true },
        { label: 'آدرس', value: item.Url },
        { label: 'نوع آدرس', value: item.UrlKind },
        { label: 'وضعیت', value: `${item.StatusCode} — ${item.StatusTitle}` },
        { label: 'مدت پردازش', value: `${this.formatNumber(item.Duration)} ms`, mono: true },
        { label: 'IP', value: item.Ip, mono: true },
        { label: 'شناسه مهمان', value: item.IdentityId, mono: true },
        { label: 'کاربر', value: item.UserId ? `${item.UserTitle} (#${item.UserId})` : 'مهمان' },
        { label: 'دستگاه', value: item.Device },
        { label: 'پلتفرم', value: item.Platform },
        { label: 'ربات', value: item.Bot || 'نیست — کاربر واقعی' },
        { label: 'منبع ورودی', value: item.Referer || 'مستقیم' },
        { label: 'User-Agent', value: item.UserAgent, mono: true },
      ]
    },

    detailBlocks() {
      const item = this.detail

      if (!item) return []

      return [
        { label: 'بدنه درخواست', value: item.Body },
        { label: 'هدرها', value: item.Header },
      ]
    },
  },
  methods: {
    formatNumber(value) {
      if (value === null || value === undefined) return '—'
      return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },

    statusVariant(code) {
      if (code >= 500) return 'light-danger'
      if (code >= 400) return 'light-warning'
      if (code >= 300) return 'light-info'
      return 'light-success'
    },

    open(row) {
      this.detail = null
      this.detailLoading = true
      this.showDetail = true

      this.$http.post(this.$api.report.trafficLogDetail, { Id: row.Id })
        .then(res => { this.detail = res.data })
        .catch(() => { this.detail = null })
        .finally(() => { this.detailLoading = false })
    },
  },
}
</script>

<style lang="scss" scoped>
.visit-log {
  &__subtitle {
    font-size: 0.75rem;
    color: #b9b9c3;
  }

  &__label {
    display: block;
    font-size: 0.75rem;
    color: #b9b9c3;
    margin-bottom: 0.25rem;
  }

  &__body {
    transition: opacity 0.15s ease;

    &--busy {
      opacity: 0.45;
      pointer-events: none;
    }
  }

  &__table ::v-deep tbody tr {
    cursor: pointer;
  }

  &__table ::v-deep td {
    padding-top: 0.45rem;
    padding-bottom: 0.45rem;
    font-size: 0.8rem;
  }

  &__mono {
    font-family: 'Courier New', monospace;
    direction: ltr;
    display: inline-block;
  }

  &__path {
    min-width: 0;
    max-width: 22rem;

    > span:first-child {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      direction: ltr;
      text-align: right;
    }
  }

  &__sub {
    display: block;
    font-size: 0.68rem;
    color: #b9b9c3;
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #b9b9c3;
    font-size: 0.85rem;
    padding: 2.5rem 0;
  }

  &__detail {
    display: grid;
    grid-template-columns: 9rem 1fr;
    gap: 0.35rem 1rem;
    margin-bottom: 0;
    font-size: 0.82rem;

    dt {
      color: #b9b9c3;
      font-weight: normal;
    }

    dd {
      margin-bottom: 0;
      word-break: break-all;
    }
  }

  &__pre {
    direction: ltr;
    text-align: left;
    max-height: 16rem;
    overflow: auto;
    font-size: 0.72rem;
    padding: 0.6rem;
    border-radius: 0.35rem;
    background: rgba(115, 103, 240, 0.06);
    white-space: pre-wrap;
    word-break: break-all;
  }
}
</style>
